import React, { useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowLeft,
  Check,
  Loader2,
  AlertCircle,
  ChevronDown,
  Building2,
  Users,
  Filter,
  TrendingUp,
  Lock,
  ShieldCheck
} from 'lucide-react';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { useToast } from '../hooks/use-toast';
import useRevealObserver from '../hooks/useRevealObserver';
import AITestimonyBlock from '../components/AITestimonyBlock';
import { submitLead } from '../lib/api';

const Investors = () => {
  useRevealObserver();
  const { toast } = useToast();
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState({});
  const formRef = useRef(null);
  const errorSummaryRef = useRef(null);

  const [form, setForm] = useState({
    fullName: '',
    email: '',
    phone: '',
    investmentRange: '',
    notes: ''
  });

  const handle = (key) => (e) => {
    setForm({ ...form, [key]: e.target ? e.target.value : e });
    if (errors[key]) setErrors({ ...errors, [key]: undefined });
  };

  const validate = () => {
    const errs = {};
    if (!form.fullName.trim()) errs.fullName = 'יש להזין שם מלא';
    if (!form.email.trim()) errs.email = 'יש להזין כתובת אימייל';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) errs.email = 'כתובת אימייל לא תקינה';
    if (!form.phone.trim()) errs.phone = 'יש להזין מספר טלפון';
    return errs;
  };

  const submit = async (e) => {
    e.preventDefault();
    const errs = validate();
    setErrors(errs);
    if (Object.keys(errs).length > 0) {
      toast({ title: 'שדות חובה חסרים', description: 'אנא בדקו את השדות המסומנים' });
      setTimeout(() => errorSummaryRef.current?.focus(), 50);
      return;
    }
    setSubmitting(true);
    try {
      // Map to existing /api/leads schema
      const payload = {
        fullName: form.fullName,
        email: form.email,
        phone: form.phone,
        company: 'משקיע פוטנציאלי',
        role: form.investmentRange ? `סדר גודל השקעה: ${form.investmentRange}` : 'משקיע',
        challenge: form.notes || undefined,
        source: 'investor-landing'
      };
      Object.keys(payload).forEach((k) => payload[k] === undefined && delete payload[k]);
      await submitLead(payload);
      setSubmitted(true);
      toast({ title: 'תודה רבה', description: 'נחזור אליכם בהקדם לתיאום פגישת המשקיעים.' });
    } catch (err) {
      toast({ title: 'שגיאה בשליחה', description: err?.response?.data?.detail || 'נסו שוב.' });
    } finally {
      setSubmitting(false);
    }
  };

  const scrollToForm = (e) => {
    e?.preventDefault?.();
    formRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    setTimeout(() => document.getElementById('inv-fullName')?.focus(), 600);
  };

  const renderField = ({ id, label, value, onChange, type = 'text', required = false, autoComplete }) => {
    const hasError = !!errors[id];
    return (
      <div className="flex flex-col gap-2">
        <Label htmlFor={`inv-${id}`} className="eyebrow text-navy/60">
          {label}{required && <span className="text-destructive mr-1" aria-hidden="true"> *</span>}
        </Label>
        <Input
          id={`inv-${id}`}
          name={id}
          type={type}
          value={value}
          onChange={onChange}
          autoComplete={autoComplete}
          aria-required={required}
          aria-invalid={hasError}
          aria-describedby={hasError ? `inv-${id}-error` : undefined}
          className={`input-clean ${hasError ? 'border-b-destructive' : ''}`}
        />
        {hasError && (
          <p id={`inv-${id}-error`} className="text-destructive text-sm flex items-center gap-1.5 mt-1" role="alert">
            <AlertCircle className="w-3.5 h-3.5" aria-hidden="true" />{errors[id]}
          </p>
        )}
      </div>
    );
  };

  // === THANK YOU ===
  if (submitted) {
    return (
      <div className="min-h-screen bg-cream flex flex-col">
        <header className="border-b border-navy/10 py-5 px-6 lg:px-12" role="banner">
          <Link to="/" className="font-serif text-xl text-navy">
            Gal<span className="text-bronze" aria-hidden="true">&</span>Blizovsky
          </Link>
        </header>
        <main className="flex-1 flex items-center justify-center px-6 py-20" role="main">
          <div className="max-w-2xl text-center">
            <div className="w-16 h-16 mx-auto bg-bronze/20 flex items-center justify-center mb-8" aria-hidden="true">
              <Check className="w-8 h-8 text-bronze" />
            </div>
            <h1 className="display-1 text-navy mb-6">תודה רבה.</h1>
            <p className="lede text-navy/70 text-lg mb-10">
              קיבלנו את פנייתכם. ציון או רז ייצרו אתכם קשר אישית תוך 1-2 ימי עסקים
              לתיאום פגישת אבחון אסטרטגית בה יוצג המודל הפיננסי המלא.
            </p>
            <Link
              to="/"
              className="btn-cta inline-flex items-center gap-3 bg-navy text-cream px-6 py-4 text-sm tracking-wide hover:bg-navy-700 transition-colors"
            >
              חזרה לעמוד הבית
              <ArrowLeft className="arrow w-4 h-4" aria-hidden="true" />
            </Link>
          </div>
        </main>
      </div>
    );
  }

  // === MAIN PAGE ===
  const pillars = [
    {
      icon: Lock,
      num: '01',
      title: 'הבעלות היא שלנו (Asset Ownership)',
      body: 'אנחנו מקימים את הנכס — המותג, האתר, ומערכות המעקב. אם ספק הקצה (קבלן/רופא) לא עומד ביעדים או מפגר בתשלום — בלחיצת כפתור (Tenant Switching) הלידים עוברים למתחרה שלו. הנכס והכוח תמיד נשארים אצלנו.'
    },
    {
      icon: Users,
      num: '02',
      title: 'ההאק הפסיכולוגי (Presenter ROI)',
      body: 'במקום לשרוף הון על פרסום "קר" ורגיל, אנחנו מכניסים למותגים פרזנטורים ויח״צ ברמה ארצית (למשל: אסף גרניט לקבלנים, מנחם הורוביץ לפיננסים). התוצאה? עלות רכישת הלקוח (CAC) נחתכת ב-30% עד 50%, ואחוזי ההמרה טסים לשמיים.'
    },
    {
      icon: Filter,
      num: '03',
      title: 'מסננת הברזל (AI & DNI)',
      body: 'אנחנו שולחים ללקוחות הקצה שלנו רק לקוחות רותחים ומוכנים לסגירה, שעברו סינון קפדני. כל שיחה מוקלטת, מנוטרת ומשויכת משפטית למקור — אין דבר כזה "לא קיבלתי את הליד".'
    },
    {
      icon: TrendingUp,
      num: '04',
      title: 'המכפיל (Trust-Based Rev-Share)',
      body: 'אנחנו לא מתעסקים בלידים של 50 שקלים. אנחנו פועלים רק בנישות ה-High-Ticket (רפואה פלסטית, מקרקעין, שיפוצי פרימיום). עסקת פלסטיקה אחת שנסגרת מכניסה עמלה של 2,000-6,000 ש״ח. עסקת שיפוץ פרימיום מכניסה 5,000-50,000 ש״ח עמלה. עסקה אחת מכסה עלות פרסום של עשרות לידים.'
    }
  ];

  const portfolio = [
    {
      name: 'Teken Zahav',
      hebrew: 'תקן זהב',
      domain: 'שיפוצי פרימיום',
      promise: 'אפס חאפרים. בקרה הנדסית על כל שיפוץ.',
      economics: 'עמלות סגירה צפויות: 5%-10% מפרויקטים של מאות אלפי שקלים.'
    },
    {
      name: 'MedMatch',
      hebrew: 'אסתטיקה בטוחה',
      domain: 'רפואה אסתטית',
      promise: 'פלטפורמת סינון למנתחים פלסטיים — ביטחון ויוקרה.',
      economics: 'עמלות סגירה צפויות: 10%-15% מעסקאות של 30,000 ש״ח ומעלה.'
    },
    {
      name: 'המאזניים',
      hebrew: 'פיננסי',
      domain: 'משכנתאות ויעוץ פיננסי',
      promise: 'כלבי השמירה של המשכנתאות.',
      economics: 'מנתבים לקוחות ליועצי פרימיום בלעדיים ולוקחים נתח קבוע מעמלות הייעוץ הגבוהות.'
    }
  ];

  return (
    <div className="min-h-screen bg-cream" dir="rtl">
      {/* Minimal header */}
      <header className="border-b border-navy/10 bg-cream/95 backdrop-blur-md sticky top-0 z-40" role="banner">
        <div className="max-w-[1200px] mx-auto px-6 lg:px-12 py-5 flex items-center justify-between">
          <Link to="/" className="font-serif text-xl lg:text-2xl text-navy" aria-label="Gal&Blizovsky - חזרה לעמוד הבית">
            Gal<span className="text-bronze" aria-hidden="true">&</span>Blizovsky
          </Link>
          <button
            type="button"
            onClick={scrollToForm}
            className="inline-flex items-center gap-2 bg-navy text-cream px-5 py-3 text-sm tracking-wide hover:bg-bronze hover:text-navy transition-colors"
          >
            <span className="hidden sm:inline">פגישת משקיעים</span>
            <span className="sm:hidden">צור קשר</span>
            <ArrowLeft className="w-4 h-4" aria-hidden="true" />
          </button>
        </div>
      </header>

      <main role="main">
        {/* HERO */}
        <section className="dark-section py-20 lg:py-32 relative overflow-hidden" aria-labelledby="inv-hero-heading">
          <div className="absolute inset-0 grain pointer-events-none" />
          <div className="max-w-[1000px] mx-auto px-6 lg:px-12 relative">
            <p className="eyebrow text-bronze mb-8">למשקיעים בלבד · אזור מוגן</p>
            <h1 id="inv-hero-heading" className="display-1 text-cream text-balance leading-[1.08] mb-10">
              בזמן ששוק הנדל״ן הפיזי מדמם תשואות של{' '}
              <span className="text-bronze">3% בשנה</span>,<br />
              בנינו מודל השקעה המייצר אקזיטים ורווחיות פסיבית מ״נדל״ן דיגיטלי״ —
              במינימום סיכון ובשקיפות הנדסית.
            </h1>
            <p className="lede text-cream/85 text-lg lg:text-xl max-w-3xl mb-10">
              שילוב נדיר בין בעלות מוחלטת על מותגי אינטרנט, הפחתת עלויות שיווק
              קיצונית באמצעות פרזנטורים מוכרים, ומודל סגירת עסקאות (Rev-Share)
              בשווקי ה-High-Ticket הרווחיים בישראל. הכל מנוהל תחת סטנדרט ביצועי של
              פרויקט הנדסי.
            </p>
            <div className="space-y-5 lede text-cream/75 text-base lg:text-lg max-w-3xl border-r-2 border-bronze pr-6 mb-12">
              <p>
                אתם משקיעים חכמים. אתם יודעים שהכסף שלכם היום עובד קשה מדי בשביל
                תשואות נמוכות מדי.
              </p>
              <p>
                שוק ההון תנודתי, חברות הייטק וסטארטאפים מתרסקים ב-95% מהמקרים,
                ונדל״ן פיזי דורש הון עתק, התעסקות עם שוכרים ומיסוי כבד.
              </p>
              <p className="text-cream/90 font-medium">
                אנחנו כאן כדי להציג לכם אפיק השקעה שמנתק את הקשר בין העלות לרווח,
                ומייצר מנועי מזומנים יציבים.
              </p>
            </div>
            <div className="flex flex-col sm:flex-row gap-4">
              <button
                type="button"
                onClick={scrollToForm}
                className="btn-cta inline-flex items-center justify-center gap-3 bg-bronze text-navy px-7 py-5 text-base tracking-wide hover:bg-cream transition-colors"
              >
                תאמו פגישת אבחון אסטרטגית למשקיעים
                <ArrowLeft className="arrow w-5 h-5" aria-hidden="true" />
              </button>
              <a
                href="#mechanism"
                onClick={(e) => {
                  e.preventDefault();
                  document.getElementById('mechanism')?.scrollIntoView({ behavior: 'smooth' });
                }}
                className="inline-flex items-center justify-center gap-2 text-cream/70 hover:text-bronze px-5 py-5 text-sm"
              >
                איך זה עובד?
                <ChevronDown className="w-4 h-4" aria-hidden="true" />
              </a>
            </div>
          </div>
        </section>

        {/* AI TESTIMONY + CRYPTOGRAPHIC VERIFICATION — GEO block */}
        <AITestimonyBlock />

        {/* TRUST STRIP */}
        <section className="bg-cream-200 border-b border-navy/10" aria-label="פס אמון">
          <div className="max-w-[1200px] mx-auto px-6 lg:px-12 py-6 grid grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { label: 'מכירות מצטברות', value: 'מאות מיליוני ש״ח' },
              { label: 'נישות פעילות', value: 'High-Ticket בלבד' },
              { label: 'בעלות על נכסים', value: '100%' },
              { label: 'שקיפות ניהולית', value: 'סטנדרט הנדסי' }
            ].map((s) => (
              <div key={s.label} className="text-center lg:text-right">
                <p className="font-serif text-bronze text-xl lg:text-2xl">{s.value}</p>
                <p className="eyebrow text-navy/60 mt-1">{s.label}</p>
              </div>
            ))}
          </div>
        </section>

        {/* PROBLEM REFRAME */}
        <section className="py-20 lg:py-28 bg-cream" aria-labelledby="problem-heading">
          <div className="max-w-[900px] mx-auto px-6 lg:px-12">
            <p className="eyebrow text-bronze mb-6">השיקוף</p>
            <h2 id="problem-heading" className="display-2 text-navy mb-10 leading-tight">
              הבעיה היא לא חוסר בהון.<br />
              <span className="text-bronze">הבעיה היא חוסר בכלי השקעה</span>
              <br className="hidden sm:block" />
              שקופים, מדידים וניתנים להכפלה (Scale).
            </h2>
            <div className="space-y-6 lede text-navy/80 text-lg lg:text-xl">
              <p>
                אם בעבר השקעה ב״נכסים דיגיטליים״ נשמעה כמו משהו ששמור לגיקים או
                לחברות ענק, היום הכלכלה השתנתה.
              </p>
              <p>
                הבעיה בשוק השיווק והלידים המסורתי היא <strong className="text-navy">חוסר שליטה</strong>.
                רוב סוכנויות הדיגיטל בונות תשתיות עבור הלקוח — ואז הלקוח עוזב,
                וההכנסה נעצרת. משקיעים שניסו להיכנס לעולם הזה נכוו כי לא היה להם
                מושג איך למדוד באמת מאיפה מגיע הכסף. הכול היה מבוסס על "הבטחות"
                ודוחות שיווקיים מעורפלים.
              </p>
              <p className="font-medium text-navy text-xl lg:text-2xl font-serif border-r-4 border-bronze pr-6 mt-10">
                אתם מחפשים מקום שבו הכסף שלכם קונה תשתית אמיתית — כזו שנשארת
                בבעלותכם המלאה, ושלא תלויה באף גחמה של לקוח קצה.
              </p>
            </div>
          </div>
        </section>

        {/* THE MECHANISM - 4 pillars */}
        <section id="mechanism" className="py-20 lg:py-28 bg-cream-200 scroll-mt-20" aria-labelledby="mechanism-heading">
          <div className="max-w-[1100px] mx-auto px-6 lg:px-12">
            <p className="eyebrow text-bronze mb-6">המנגנון הייחודי</p>
            <h2 id="mechanism-heading" className="display-2 text-navy mb-6 leading-tight">
              הכירו את <span className="text-bronze">מודל משפך הערך המרובע</span>:
            </h2>
            <p className="lede text-navy/75 text-lg lg:text-xl mb-16 max-w-3xl">
              נדל״ן דיגיטלי מבוסס פרזנטורים וחלוקת רווחים. Gal&Blizovsky לא מקימה
              עוד "סוכנות" — אנחנו מקימים <strong className="text-navy">רשת של מותגי נישה בבעלותנו המלאה</strong>,
              ונעזרים בהון שלכם כדי לשים עליהם גז. הנה איך המערכת עובדת בדיוק
              מתמטי:
            </p>

            <div className="grid lg:grid-cols-2 gap-px bg-navy/10">
              {pillars.map((p) => (
                <article key={p.num} className="bg-cream-200 p-8 lg:p-10 lift-card">
                  <div className="flex items-start gap-5 mb-5">
                    <div className="w-14 h-14 bg-navy text-bronze flex items-center justify-center flex-shrink-0">
                      <p.icon className="w-6 h-6" strokeWidth={1.5} aria-hidden="true" />
                    </div>
                    <div className="flex-1">
                      <p className="font-serif text-bronze text-2xl mb-1" aria-hidden="true">{p.num}</p>
                      <h3 className="display-3 text-navy">{p.title}</h3>
                    </div>
                  </div>
                  <p className="lede text-navy/75 text-base lg:text-lg pr-[76px]">{p.body}</p>
                </article>
              ))}
            </div>
          </div>
        </section>

        {/* AUTHORITY */}
        <section className="py-20 lg:py-28 bg-cream" aria-labelledby="auth-heading">
          <div className="max-w-[1100px] mx-auto px-6 lg:px-12">
            <p className="eyebrow text-bronze mb-6">ההנהלה</p>
            <h2 id="auth-heading" className="display-2 text-navy mb-12 leading-tight max-w-3xl">
              השקעות מצליחות בזכות האנשים שמנהלים אותן.<br />
              הכירו את ההנהלה שעומדת מאחורי המכונה.
            </h2>
            <p className="lede text-navy/75 text-lg max-w-3xl mb-12">
              משקיע לא שם כסף על רעיון, הוא שם כסף על יכולת ביצוע. Gal&Blizovsky
              משלבת בין מומחיות דיגיטלית חסרת תקדים לבין יכולות ניהול הנדסיות-ביצועיות,
              המבטיחות שכספי המשקיעים מנוהלים בחרדת קודש של פרויקט לאומי.
            </p>

            <div className="grid lg:grid-cols-2 gap-px bg-navy/10">
              <article className="bg-cream p-8 lg:p-10">
                <p className="eyebrow text-bronze mb-3">מנכ״ל החברה — הזרוע הניהולית-הנדסית</p>
                <h3 className="display-3 text-navy mb-5">ציון גל</h3>
                <p className="lede text-navy/75 text-base lg:text-lg">
                  כמהנדס מומחה ומהראשונים שעיצבו את התחום הסולארי בישראל, ציון ניהל
                  אלפי פרויקטים מורכבים מול הגופים המחמירים במשק (משרד רה״מ, בנקים,
                  רשויות). בעולם הדיגיטל, שבו כולם מאלתרים, ציון פועל תחת אסטרטגיה
                  אחת: <strong className="text-navy">שיווק הוא מדע</strong>.
                  הוא מחדיר למערך הדיגיטלי סטנדרט של ניהול סיכונים, בקרה, ובדיקת
                  תשואות הנדסית: בודקים קלט, מודדים פלט (ROI), ומבטיחים תוצאה למשקיע.
                </p>
              </article>
              <article className="bg-cream p-8 lg:p-10">
                <p className="eyebrow text-bronze mb-3">סמנכ״ל דיגיטל וצמיחה — הזרוע הדיגיטלית-שיווקית</p>
                <h3 className="display-3 text-navy mb-5">רז בליזובסקי</h3>
                <p className="lede text-navy/75 text-base lg:text-lg">
                  רז חי ונושם צמיחה. עם ניסיון בהובלת מהלכי מכירה ושיווק בהיקפים
                  מצטברים של <strong className="text-navy">מאות מיליוני שקלים</strong>,
                  והקמת קהילות משווקים מהמשמעותיות בישראל (מעל 1,000 אנשי מקצוע
                  שלמדו משיטותיו). רז אחראי על בניית המכונה הדיגיטלית שחותכת את
                  עלויות הרכישה, מביאה את הטראפיק, וגורמת לפסיכולוגיית הלקוח הישראלי
                  לפעול לטובתנו.
                </p>
              </article>
            </div>
          </div>
        </section>

        {/* PORTFOLIO */}
        <section className="py-20 lg:py-28 bg-cream-200" aria-labelledby="portfolio-heading">
          <div className="max-w-[1100px] mx-auto px-6 lg:px-12">
            <p className="eyebrow text-bronze mb-6">פורטפוליו · Sneak Peek</p>
            <h2 id="portfolio-heading" className="display-2 text-navy mb-12 leading-tight max-w-3xl">
              לאן הולך הכסף?<br />
              דוגמאות למותגים <span className="text-bronze">שאנחנו מקימים ממש עכשיו:</span>
            </h2>

            <div className="grid lg:grid-cols-3 gap-px bg-navy/10 mb-12">
              {portfolio.map((b) => (
                <article key={b.name} className="bg-cream-200 p-8 lg:p-10 lift-card flex flex-col">
                  <p className="eyebrow text-bronze mb-3">{b.domain}</p>
                  <h3 className="font-serif text-3xl lg:text-4xl text-navy mb-1 tracking-tight">{b.name}</h3>
                  <p className="text-navy/60 text-sm mb-6">({b.hebrew})</p>
                  <p className="font-serif text-navy/90 italic mb-6 text-lg">״{b.promise}״</p>
                  <p className="text-navy/75 text-sm leading-relaxed border-t border-navy/15 pt-5 mt-auto">
                    {b.economics}
                  </p>
                </article>
              ))}
            </div>

            <div className="bg-navy text-cream p-8 lg:p-10 relative overflow-hidden">
              <div className="absolute inset-0 grain pointer-events-none" />
              <div className="relative flex items-start gap-5">
                <Building2 className="w-8 h-8 text-bronze flex-shrink-0 mt-1" strokeWidth={1.5} aria-hidden="true" />
                <p className="font-serif text-xl lg:text-2xl leading-snug">
                  כל אחד מהנכסים האלו, ברגע שהוא מיוצב, ניתן למכירה ומימוש (אקזיט)
                  ב<span className="text-bronze">מכפילי רווח של פי 30</span> מהרווח
                  החודשי הפסיבי שלו בשוק החופשי.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* OFFER + FORM */}
        <section
          ref={formRef}
          id="form"
          className="py-20 lg:py-28 bg-cream scroll-mt-20"
          aria-labelledby="offer-heading"
        >
          <div className="max-w-[1200px] mx-auto px-6 lg:px-12">
            <div className="grid lg:grid-cols-12 gap-12 lg:gap-16">
              <div className="lg:col-span-5">
                <p className="eyebrow text-bronze mb-6">ההצעה</p>
                <h2 id="offer-heading" className="display-2 text-navy mb-8 leading-tight">
                  ההון שלכם, התשתית והניהול שלנו.<br />
                  <span className="text-bronze">בואו נדבר על מספרים.</span>
                </h2>
                <div className="space-y-5 lede text-navy/75 text-base lg:text-lg">
                  <p>
                    אנחנו מחפשים שותפים לדרך שמבינים שעידן השיווק המסורתי מת, ועידן
                    הנכסים הדיגיטליים בבעלות פרטית הוא הדבר הגדול הבא.
                  </p>
                  <p>
                    אנו מגייסים הון המיועד נטו למימון תקציבי המדיה, ההפקה והפרזנטורים —
                    בזמן שאנחנו מביאים את התשתית, הטכנולוגיה, הניהול השוטף וסגירת
                    עסקאות הקצה במודל חלוקת רווחים.
                  </p>
                </div>

                <div className="grid grid-cols-2 gap-px bg-navy/10 mt-10">
                  {[
                    ['חלוקה', 'Rev-Share'],
                    ['בעלות', '100% Gal&Blizovsky'],
                    ['ייעוד הון', 'מדיה · הפקה · פרזנטורים'],
                    ['התחייבות', 'ללא חשיפה תפעולית']
                  ].map(([k, v]) => (
                    <div key={k} className="bg-cream p-4">
                      <p className="eyebrow text-navy/60 mb-1">{k}</p>
                      <p className="font-serif text-navy text-base">{v}</p>
                    </div>
                  ))}
                </div>
              </div>

              <div className="lg:col-span-7">
                <form
                  onSubmit={submit}
                  className="bg-cream-200 p-8 lg:p-12 border border-navy/15 space-y-7"
                  noValidate
                  aria-label="טופס פגישת אבחון למשקיעים"
                >
                  <div>
                    <p className="font-serif text-2xl lg:text-3xl text-navy mb-2 flex items-center gap-3">
                      <ShieldCheck className="w-6 h-6 text-bronze" aria-hidden="true" strokeWidth={1.8} />
                      פגישת משקיעים סודית
                    </p>
                    <p className="text-navy/60 text-sm">
                      טופס קביעת פגישת אבחון אסטרטגית — מיועד למשקיעים פוטנציאליים בלבד.
                    </p>
                  </div>

                  {Object.keys(errors).length > 0 && (
                    <div
                      ref={errorSummaryRef}
                      tabIndex={-1}
                      role="alert"
                      aria-live="assertive"
                      className="bg-destructive/10 border border-destructive/40 p-4 outline-none"
                    >
                      <p className="text-destructive font-medium mb-2 flex items-center gap-2">
                        <AlertCircle className="w-4 h-4" aria-hidden="true" />
                        יש לתקן {Object.keys(errors).length} שדות
                      </p>
                      <ul className="text-destructive/90 text-sm space-y-1 mr-6 list-disc">
                        {Object.entries(errors).map(([k, v]) => (
                          <li key={k}>{v}</li>
                        ))}
                      </ul>
                    </div>
                  )}

                  <div className="grid sm:grid-cols-2 gap-6">
                    {renderField({
                      id: 'fullName',
                      label: 'שם מלא',
                      value: form.fullName,
                      onChange: handle('fullName'),
                      required: true,
                      autoComplete: 'name'
                    })}
                    {renderField({
                      id: 'email',
                      label: 'דוא״ל',
                      value: form.email,
                      onChange: handle('email'),
                      type: 'email',
                      required: true,
                      autoComplete: 'email'
                    })}
                    {renderField({
                      id: 'phone',
                      label: 'טלפון',
                      value: form.phone,
                      onChange: handle('phone'),
                      type: 'tel',
                      required: true,
                      autoComplete: 'tel'
                    })}
                    <div className="flex flex-col gap-2">
                      <Label htmlFor="inv-range" className="eyebrow text-navy/60">
                        סדר גודל השקעה רלוונטי
                      </Label>
                      <Select value={form.investmentRange} onValueChange={(v) => setForm({ ...form, investmentRange: v })}>
                        <SelectTrigger
                          id="inv-range"
                          aria-label="סדר גודל השקעה רלוונטי"
                          className="bg-transparent border-0 border-b border-navy/20 rounded-none px-0 focus:ring-0 focus:border-navy"
                        >
                          <SelectValue placeholder="בחרו טווח" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="100K-300K">100,000-300,000 ש״ח</SelectItem>
                          <SelectItem value="300K-500K">300,000-500,000 ש״ח</SelectItem>
                          <SelectItem value="500K+">500,000 ש״ח ומעלה</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div className="flex flex-col gap-2">
                    <Label htmlFor="inv-notes" className="eyebrow text-navy/60">
                      הערות / רקע קצר על פעילות ההשקעות שלך עד כה
                    </Label>
                    <Textarea
                      id="inv-notes"
                      name="notes"
                      value={form.notes}
                      onChange={handle('notes')}
                      rows={4}
                      className="bg-transparent border-0 border-b border-navy/20 rounded-none px-0 py-2 focus-visible:ring-0 focus-visible:border-navy resize-none"
                    />
                  </div>

                  <button
                    type="submit"
                    disabled={submitting}
                    aria-busy={submitting}
                    className="btn-cta inline-flex items-center justify-center gap-3 bg-navy text-cream px-8 py-5 text-base tracking-wide hover:bg-bronze hover:text-navy transition-colors duration-300 w-full disabled:opacity-60"
                  >
                    {submitting ? (
                      <>
                        <Loader2 className="w-5 h-5 animate-spin" aria-hidden="true" />
                        שולח...
                      </>
                    ) : (
                      <>
                        קבעו פגישת משקיעים סודית עם ציון ורז
                        <ArrowLeft className="arrow w-5 h-5" aria-hidden="true" />
                      </>
                    )}
                  </button>

                  <p className="text-navy/55 text-xs leading-relaxed border-t border-navy/10 pt-4">
                    פנייה זו אינה מהווה התחייבות. הפגישה מיועדת לבחינת התאמה הדדית
                    והצגת המודל הפיננסי המלא. כל המידע שיועבר במהלך הפגישה כפוף
                    להסכם סודיות.
                  </p>
                </form>
              </div>
            </div>
          </div>
        </section>

        {/* Closing tag */}
        <section className="dark-section py-12 relative overflow-hidden" aria-label="חתימת המותג">
          <div className="absolute inset-0 grain pointer-events-none" />
          <div className="max-w-[1100px] mx-auto px-6 lg:px-12 text-center relative">
            <p className="font-serif italic text-cream/80 text-lg lg:text-xl">
              Gal<span className="text-bronze" aria-hidden="true">&</span>Blizovsky —
              <span className="text-bronze"> הנדסת מנגנוני צמיחה לעסקים ולמשקיעים.</span>
            </p>
          </div>
        </section>
      </main>

      <footer className="bg-navy text-cream/60 py-6" role="contentinfo">
        <div className="max-w-[1100px] mx-auto px-6 lg:px-12 flex flex-col sm:flex-row justify-between gap-3 text-xs">
          <p>© {new Date().getFullYear()} Gal&Blizovsky. כל הזכויות שמורות.</p>
          <div className="flex gap-6">
            <Link to="/" className="hover:text-bronze">עמוד הבית</Link>
            <Link to="/about" className="hover:text-bronze">מי אנחנו</Link>
            <a href="mailto:office@gal-bl.co.il" className="hover:text-bronze">office@gal-bl.co.il</a>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Investors;
